import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert, Button } from 'reactstrap';
import config from '../../config';
import s from './KnowledgeDocuments.module.scss';

const POLLING_INTERVAL = 30000; // 30s
const refreshIntervalSeconds = POLLING_INTERVAL / 1000;

const DOC_TYPE_CONFIG = {
  policy: { label: 'Policy', icon: 'P' },
  faq: { label: 'FAQ', icon: 'Q' },
  sop: { label: 'SOP', icon: 'S' },
  serviceability: { label: 'Serviceability', icon: 'A' },
  pricing_guidance: { label: 'Pricing', icon: '$' },
};

// ============================================================================
// EXAMPLE DOCUMENTS (from knowledge_docs/ seed files)
// ============================================================================
const EXAMPLE_DOCUMENTS = {
  'booking_faq': {
    label: 'Booking & Vehicle FAQ',
    title: 'Booking & Vehicle FAQs',
    doc_type: 'faq',
    scope: 'global',
    locale: 'en',
    version: new Date().toISOString().split('T')[0],
    body: `## Summary

**Q: How do I book a truck?**
**Q: Can I modify a booking?**
**Q: What vehicles are available?**

## Body

**Q: How do I book a truck?**
A: Tell us three things: (1) pickup time, (2) pickup and drop address, and (3) vehicle type or load weight. We'll calculate the price and confirm. It takes about a minute on WhatsApp.

**Q: Can I modify a booking after confirmation?**
A: Yes, you can modify booking if done 2 hours before pickup. You can change time, address, or vehicle. Within 2 hours of pickup, the booking may need to be cancelled and rebooked. Message us on this chat with the changes.

**Q: What vehicles are available?**
A: We have Goods Auto (0.5 ton / 5 ft), Tata Ace (1.0 ton / 7 ft), Bolero Pickup (1.5 ton / 8 ft), Bada Dost (2.0 ton / 9 ft), Tata 407 (2.5 ton / 10 ft), and larger trucks up to 10 tons. We will shortly launch part-load (per-box) and JCB/crane hourly bookings.

**Q: How much does it cost?**
A: Pricing depends on distance, vehicle type and tolls. Share your pickup and drop addresses to get an exact estimate. There are no hidden charges.

**Q: How far in advance should I book?**
A: For scheduled bookings, at least 4 hours ahead is recommended. We also handle instant/urgent bookings — tell us "urgent" and we'll find the nearest available vehicle.

**Q: What documents do I need?**
A: Nothing special. Just be present at pickup with your goods ready to load. For B2B accounts, your business registration is already verified.

**Q: Do you load and unload?**
A: Driver assists with loading/unloading guidance but does not provide labor. If you need loading help, either you can arrange the service with us or employ porters separately. For heavy equipment (JCB/crane), the operator handles the machinery.`,
  },
  'cancellation_policy': {
    label: 'Cancellation & Refund Policy',
    title: 'Cancellation & Refund Policy',
    doc_type: 'policy',
    scope: 'global',
    locale: 'en',
    version: new Date().toISOString().split('T')[0],
    body: `## Summary
You can cancel anytime before the driver reaches pickup. And if unavoidable circumstances then even after. No cancellation fee is charged at UNLOADIN.

## Body

## Before driver is dispatched
No cancellation fee. You will be notified when the driver has been dispatched to your pickup location.

## After driver is dispatched but before reaching pickup
No cancellation fee. However we advise you to let us know before to avoid inconvenience to driver partners.

## After driver reaches pickup
No cancellation fee. However we advise you to let us know before to avoid inconvenience to driver partners. Repeated cancellations might lead to review of future bookings.

## No-shows
If the driver waits 30 minutes at pickup and cannot reach you, it is treated as a cancellation after arrival.

## Refund timeline
UNLOADIN does not take any advance charges. However after completion of service if there is a valid case for refund of service amount, it will be honored. Refunds are processed within 5-7 business days to your original payment method. B2B accounts get credit notes applied to their next invoice.

## How to cancel
Message us on this WhatsApp chat with "cancel booking" and your booking ID. No separate phone call needed.

## Modifications
You can change pickup time, address, or drop location before the driver is on the way to the pickup location. After that, the booking may need to be cancelled and rebooked. Message us on this chat to modify.`,
  },
  'pricing_guidance': {
    label: 'How Pricing is Calculated',
    title: 'How Pricing is Calculated',
    doc_type: 'pricing_guidance',
    scope: 'global',
    locale: 'en',
    version: new Date().toISOString().split('T')[0],
    body: `## Summary
Pricing depends on distance, time taken to reach destination, vehicle type, tolls, and booking type. Share pickup and drop to get an exact estimate.

## Body

## How it works
Pricing is distance-based. The formula is:
- Base price (fixed cost for the vehicle)
- Distance and time taken to reach destination
- Tolls (actual toll charges on the route)
- Waiting charges (if the driver waits beyond included time, typically 30 minutes of smaller vehicle and higher for bigger trucks)

The total is calculated when you share your pickup and drop. The estimate shown before confirmation is what you pay — no hidden charges.

## Full truck vs part-load pricing
- Full truck: you pay for the entire vehicle at a per-km rate. Best for larger shipments.
- Part-load: you pay per box at a fixed route rate. Best for small shipments (under 500 kg).
- Hourly / JCB: charged per hour with a minimum duration (typically 4 hours for JCB, 2 hours for trucks).

## What affects the price
- Distance and time, between pickup and drop (longer = more)
- Vehicle size (larger truck = higher per-km rate)
- Tolls on the route (actual cost, not marked up)

## Discounts
- Early-booking discount (10%) for bookings made at least 24 hours in advance
- Flex booking can get up to 25% discount as we pool bookings to provide best rate
- B2B accounts may have negotiated volume discounts applied automatically
- No separate promo codes or coupon system at this time

## Getting an estimate
Share your exact pickup and drop addresses to get a precise estimate. City-level approximate rates are available for reference but actual booking uses exact addresses.`,
  },
  'serviceability': {
    label: 'Karnataka Service Area',
    title: 'Karnataka Service Area',
    doc_type: 'serviceability',
    scope: 'global',
    locale: 'en',
    version: new Date().toISOString().split('T')[0],
    body: `## Summary
We currently serve Karnataka. Both pickup and drop must be within serviceable areas.

## Body

## Service area
We operate across Karnataka state, including major cities and surrounding districts:
- Bangalore (urban and extended areas up to 50 km radius)
- Mysore, Mandya, Hassan
- Mangalore, Udupi, Manipal, Kundapura
- Hubli, Dharwad, Belgaum
- Davangere, Shimoga, Chitradurga
- Tumkur, Kolar, Chikkaballapur
- Coastal Karnataka: Karwar, Bhatkal, Kundapura, Udupi, Mangalore

## Not currently served
- Outside Karnataka (interstate transport is not available)
- Remote villages without road access
- Certain forest/protected areas

## How serviceability is confirmed
We will check your exact address when you share it. An address inside the service area will be accepted immediately. If an address is outside the service area, we will inform you and suggest alternatives if available.

## Important
- Final serviceability is confirmed when you share your address or location pin
- City-level estimates are indicative only — exact addresses are verified before booking
- The service area may expand over time. Ask the bot about specific areas.`,
  },
  'driver_delay_sop': {
    label: 'Driver Delay Complaint SOP',
    title: 'Driver Delay Complaint Handling',
    doc_type: 'sop',
    scope: 'global',
    locale: 'en',
    version: new Date().toISOString().split('T')[0],
    body: `## Summary
Standard operating procedure for handling customer complaints about driver delays.

## Body

## Step 1: Acknowledge the complaint
Respond within 2 minutes. Acknowledge the delay and apologize for the inconvenience.

## Step 2: Check driver status
Check the driver's live location and ETA. If the driver is en route, share the updated ETA with the customer.

## Step 3: Offer options
If the delay is more than 15 minutes:
- Offer to cancel and rebook with a different driver
- Offer a discount on the current booking
- Let the customer decide

## Step 4: Follow up
After the booking is complete, send a follow-up message to ensure customer satisfaction.

## Escalation
If the customer is not satisfied, escalate to the ops team for manual review.`,
  },
};

export default function KnowledgeDocuments() {
  const history = useHistory();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState({ open: false, doc: null, chunks: [] });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [form, setForm] = useState({ title: '', doc_type: 'faq', body: '', scope: 'global', locale: 'en', version: new Date().toISOString().split('T')[0] });
  const [submitting, setSubmitting] = useState(false);
  const [selectedExample, setSelectedExample] = useState('');
  const [nextRefreshIn, setNextRefreshIn] = useState(refreshIntervalSeconds);
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents`, {
        signal: abortControllerRef.current.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });
      const data = await res.json();
      setDocuments(data.documents || []);
      setNextRefreshIn(refreshIntervalSeconds);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError('Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDeletedDocuments = useCallback(async () => {
    setLoadingDeleted(true);
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents/deleted`);
      const data = await res.json();
      setDeletedDocuments(data.documents || []);
    } catch (e) {
      console.error('Failed to load deleted documents:', e);
    } finally {
      setLoadingDeleted(false);
    }
  }, []);

  const handleRestore = async (doc) => {
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents/${doc.id}/restore`, { method: 'POST' });
      if (res.ok) {
        fetchDeletedDocuments();
        fetchDocuments();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to restore document');
      }
    } catch (e) {
      setError('Failed to restore document');
    }
  };

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Auto-refresh with polling
  useEffect(() => {
    const interval = setInterval(fetchDocuments, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchDocuments]);

  // Fetch deleted documents when filter is 'deleted'
  useEffect(() => {
    if (activeFilter === 'deleted') {
      fetchDeletedDocuments();
    }
  }, [activeFilter, fetchDeletedDocuments]);

  // Countdown timer
  useEffect(() => {
    if (loading) return;
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSubmitting(true);
    try {
      const scope = form.scope === 'global' ? { scope: 'global' } : { scope: 'global', city: form.scope };
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          doc_type: form.doc_type,
          body: form.body,
          scope,
          locale: form.locale,
          version: form.version,
        }),
      });
      if (res.ok) {
        setShowAddModal(false);
        setForm({ title: '', doc_type: 'faq', body: '', scope: 'global', locale: 'en', version: new Date().toISOString().split('T')[0] });
        setSelectedExample('');
        fetchDocuments();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to ingest document');
      }
    } catch (e) {
      setError('Failed to ingest document');
    } finally {
      setSubmitting(false);
    }
  };

  const loadExample = (key) => {
    const example = EXAMPLE_DOCUMENTS[key];
    if (example) {
      setForm({
        title: example.title,
        doc_type: example.doc_type,
        body: example.body,
        scope: example.scope,
        locale: example.locale,
        version: example.version,
      });
      setSelectedExample(key);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents/${doc.id}`);
      const data = await res.json();
      setShowDetailModal({ open: true, doc: data.document, chunks: data.chunks || [] });
    } catch (e) {
      console.error('Failed to fetch document details:', e);
    }
  };

  const editDocument = async (doc) => {
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents/${doc.id}`);
      const data = await res.json();
      const chunks = data.chunks || [];
      const reconstructedBody = chunks
        .sort((a, b) => a.chunk_index - b.chunk_index)
        .map(c => c.content)
        .join('\n\n');
      const d = data.document;
      setForm({
        title: d.title || '',
        doc_type: d.doc_type || 'faq',
        body: reconstructedBody,
        scope: (d.scope && d.scope.city) ? d.scope.city : 'global',
        locale: d.locale || 'en',
        version: d.version || '',
      });
      setEditingDocId(doc.id);
      setShowEditModal(true);
    } catch (e) {
      console.error('Failed to fetch document for edit:', e);
    }
  };

  const handleUpdate = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSubmitting(true);
    try {
      const scope = form.scope === 'global' ? { scope: 'global' } : { scope: 'global', city: form.scope };
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents/${editingDocId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          doc_type: form.doc_type,
          body: form.body,
          scope,
          locale: form.locale,
          version: form.version,
        }),
      });
      if (res.ok) {
        setShowEditModal(false);
        setEditingDocId(null);
        fetchDocuments();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to update document');
      }
    } catch (e) {
      setError('Failed to update document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}" and its chunks? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-documents/${doc.id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchDocuments();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to delete document');
      }
    } catch (e) {
      setError('Failed to delete document');
    }
  };

  const markDocInReview = async (doc) => {
    try {
      await fetch(`${config.customerApiUrl}/knowledge-documents/${doc.id}/in-review`, { method: 'POST' });
      fetchDocuments();
    } catch (e) {
      console.error('Mark in-review failed:', e);
    }
  };

  const activateDoc = async (doc) => {
    try {
      await fetch(`${config.customerApiUrl}/knowledge-documents/${doc.id}/activate`, { method: 'POST' });
      fetchDocuments();
    } catch (e) {
      console.error('Activate document failed:', e);
    }
  };

  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  const stats = {
    total: documents.length,
    active: documents.filter(d => d.status === 'active').length,
    in_review: documents.filter(d => d.status === 'in_review').length,
    policy: documents.filter(d => d.doc_type === 'policy').length,
    faq: documents.filter(d => d.doc_type === 'faq').length,
    sop: documents.filter(d => d.doc_type === 'sop').length,
    serviceability: documents.filter(d => d.doc_type === 'serviceability').length,
    pricing_guidance: documents.filter(d => d.doc_type === 'pricing_guidance').length,
  };

  const filteredDocuments = activeFilter === 'deleted'
    ? deletedDocuments
    : activeFilter === 'all'
      ? documents
      : activeFilter === 'in_review'
        ? documents.filter(d => d.status === 'in_review')
        : documents.filter(d => d.doc_type === activeFilter);

  return (
    <div className={s.root}>
      <div className={s.pageBody}>
        {/* Header */}
        <div className={s.header}>
          <div className={s.headerLeft}>
            <h3 className={s.headerTitle}>Knowledge Base</h3>
            <p className={s.headerSubtitle}>Business knowledge documents used by the bot to answer questions</p>
          </div>
          <div className={s.headerRight}>
            <button
              className={s.refreshBtn}
              onClick={fetchDocuments}
              disabled={loading}
              title={`Auto-refreshing in ${nextRefreshIn}s`}
            >
              <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
              {!loading && (
                <div className={s.refreshProgressTrack}>
                  <div
                    className={s.refreshProgressBar}
                    style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / refreshIntervalSeconds) * 100))}%` }}
                  />
                </div>
              )}
            </button>
            <button className={s.journalBtn} onClick={() => history.push('/app/knowledge/booking-journal')}>
              Booking Journal
            </button>
            <button className={s.addBtn} onClick={() => setShowAddModal(true)}>
              + Add Document
            </button>
          </div>
        </div>

        {/* Stats Row — clickable filters */}
        <div className={s.statsRow}>
          <div
            className={`${s.statCard} ${s.total} ${s.clickable} ${activeFilter === 'all' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('all')}
            role="button"
            title="Show all documents"
          >
            <div className={`${s.statIcon} ${s.total}`}>&#9776;</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.total}</div>
              <div className={s.statLabel}>All Docs</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.in_review} ${s.clickable} ${activeFilter === 'in_review' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('in_review')}
            role="button"
            title="Show documents in review"
          >
            <div className={`${s.statIcon} ${s.in_review}`}>~</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.in_review}</div>
              <div className={s.statLabel}>In Review</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.policy} ${s.clickable} ${activeFilter === 'policy' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('policy')}
            role="button"
            title="Show policies"
          >
            <div className={`${s.statIcon} ${s.policy}`}>P</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.policy}</div>
              <div className={s.statLabel}>Policies</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.faq} ${s.clickable} ${activeFilter === 'faq' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('faq')}
            role="button"
            title="Show FAQs"
          >
            <div className={`${s.statIcon} ${s.faq}`}>Q</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.faq}</div>
              <div className={s.statLabel}>FAQs</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.sop} ${s.clickable} ${activeFilter === 'sop' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('sop')}
            role="button"
            title="Show SOPs"
          >
            <div className={`${s.statIcon} ${s.sop}`}>S</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.sop}</div>
              <div className={s.statLabel}>SOPs</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.serviceability} ${s.clickable} ${activeFilter === 'serviceability' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('serviceability')}
            role="button"
            title="Show serviceability docs"
          >
            <div className={`${s.statIcon} ${s.serviceability}`}>A</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.serviceability}</div>
              <div className={s.statLabel}>Serviceability</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.pricing_guidance} ${s.clickable} ${activeFilter === 'pricing_guidance' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('pricing_guidance')}
            role="button"
            title="Show pricing guidance"
          >
            <div className={`${s.statIcon} ${s.pricing_guidance}`}>$</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.pricing_guidance}</div>
              <div className={s.statLabel}>Pricing</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.clickable} ${activeFilter === 'deleted' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('deleted')}
            role="button"
            title="Show deleted documents"
            style={activeFilter === 'deleted' ? { borderColor: '#ef4444', background: 'rgba(239,68,68,0.08)' } : {}}
          >
            <div className={s.statIcon} style={{ color: '#ef4444' }}>
              <i className="fa fa-trash" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>{deletedDocuments.length}</div>
              <div className={s.statLabel}>Deleted</div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        {/* Loading */}
        {(loading || (activeFilter === 'deleted' && loadingDeleted)) ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : filteredDocuments.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>&#128196;</div>
            <h5 className={s.emptyTitle}>
              {activeFilter === 'deleted' ? 'No deleted documents' : 'No documents match this filter'}
            </h5>
            <p className={s.emptyText}>
              {activeFilter === 'deleted' ? 'Deleted documents will appear here.' : 'Try selecting a different document type.'}
            </p>
          </div>
        ) : (
          <div className={s.tableCard}>
            <div className={s.tableWrapper}>
              <table className={s.docTable}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Locale</th>
                    <th>Version</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => {
                    const dt = DOC_TYPE_CONFIG[doc.doc_type] || { label: doc.doc_type, icon: '?' };
                    return (
                       <tr key={doc.id}>
                        <td><p className={s.docTitle}>{doc.title}</p></td>
                        <td><span className={`${s.docTypeBadge} ${s[doc.doc_type] || ''}`}>{dt.label}</span></td>
                        <td><span className={s.metaText}>{doc.locale?.toUpperCase()}</span></td>
                        <td><span className={s.metaText}>{doc.version}</span></td>
                        <td><span className={s.metaText}>{formatDate(doc.updated_at)}</span></td>
                        <td>
                          {activeFilter === 'deleted' ? (
                            <>
                              <button className={s.viewBtn} onClick={() => viewDocument(doc)}>View</button>
                              {' '}
                              <button className={s.viewBtn} onClick={() => handleRestore(doc)} style={{ borderColor: '#10b981', color: '#10b981' }}>Restore</button>
                            </>
                          ) : (
                            <>
                              {doc.status === 'in_review' && (
                                <span className={`${s.statusBadge} ${s.inReviewBadge}`}>In Review</span>
                              )}
                              <button className={s.viewBtn} onClick={() => viewDocument(doc)}>View</button>
                              {' '}
                              <button className={s.viewBtn} onClick={() => editDocument(doc)}>Edit</button>
                              {' '}
                              {doc.status === 'active' && (
                                <button className={s.viewBtn} onClick={() => markDocInReview(doc)} style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>In Review</button>
                              )}
                              {' '}
                              {doc.status === 'in_review' && (
                                <button className={s.viewBtn} onClick={() => activateDoc(doc)} style={{ borderColor: '#10b981', color: '#10b981' }}>Activate</button>
                              )}
                              {' '}
                              <button className={s.viewBtn} onClick={() => handleDelete(doc)} style={{ borderColor: '#ef4444', color: '#ef4444' }}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Document Modal */}
        <Modal isOpen={showAddModal} toggle={() => { setShowAddModal(false); setSelectedExample(''); }} size="lg">
          <ModalHeader toggle={() => { setShowAddModal(false); setSelectedExample(''); }}>Add Knowledge Document</ModalHeader>
          <ModalBody>
            {/* Example Loader */}
            <div className={s.exampleLoader}>
              <span className={s.exampleLoaderLabel}>Load example:</span>
              <select
                className={s.exampleSelect}
                value={selectedExample}
                onChange={(e) => loadExample(e.target.value)}
              >
                <option value="">Choose a template...</option>
                {Object.entries(EXAMPLE_DOCUMENTS).map(([key, ex]) => (
                  <option key={key} value={key}>{ex.label}</option>
                ))}
              </select>
            </div>

            <Form>
              <div className={s.formSection}>
                <div className={s.formSectionTitle}>Document Info</div>
                <FormGroup row>
                  <Label sm={3}>Title</Label>
                  <Input
                    sm={9}
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Cancellation Policy"
                  />
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Document Type</Label>
                  <Input
                    sm={9}
                    type="select"
                    value={form.doc_type}
                    onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                  >
                    <option value="policy">Policy</option>
                    <option value="faq">FAQ</option>
                    <option value="sop">SOP</option>
                    <option value="serviceability">Serviceability</option>
                    <option value="pricing_guidance">Pricing Guidance</option>
                  </Input>
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Scope</Label>
                  <Input
                    sm={9}
                    type="select"
                    value={form.scope}
                    onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  >
                    <option value="global">Global (all customers)</option>
                    <option value="Bangalore">Bangalore</option>
                    <option value="Mangalore">Mangalore</option>
                    <option value="Udupi">Udupi</option>
                    <option value="Hubli">Hubli</option>
                    <option value="Mysore">Mysore</option>
                  </Input>
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Locale</Label>
                  <Input
                    sm={9}
                    type="select"
                    value={form.locale}
                    onChange={(e) => setForm({ ...form, locale: e.target.value })}
                  >
                    <option value="en">English</option>
                    <option value="kn">Kannada</option>
                    <option value="hi">Hindi</option>
                    <option value="ta">Tamil</option>
                  </Input>
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Version</Label>
                  <Input
                    sm={9}
                    value={form.version}
                    onChange={(e) => setForm({ ...form, version: e.target.value })}
                    placeholder="YYYY-MM-DD"
                  />
                </FormGroup>
              </div>

              <div className={s.formSection}>
                <div className={s.formSectionTitle}>Content</div>
                <FormGroup>
                  <Input
                    type="textarea"
                    rows={14}
                    className={s.bodyTextarea}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder={`## Summary\nOne sentence describing what this document covers.\n\n## Body\nYour content here.`}
                  />
                  <div className={s.formatHint}>
                    <div className={s.formatHintTitle}>Format Guide</div>
                    <p className={s.formatHintText}>
                      Use <code>## Summary</code> for a brief overview and <code>## Body</code> for the full content.<br />
                      For FAQs, use <code>**Q:**</code> and <code>A:</code> format. For policies/SOPs, use <code>## Section</code> headings.
                    </p>
                  </div>
                </FormGroup>
              </div>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => { setShowAddModal(false); setSelectedExample(''); }}>Cancel</Button>
            <Button color="primary" onClick={handleSubmit} disabled={submitting || !form.title.trim() || !form.body.trim()}>
              {submitting ? <Spinner size="sm" /> : 'Ingest Document'}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Document Modal */}
        <Modal isOpen={showEditModal} toggle={() => { setShowEditModal(false); setEditingDocId(null); }} size="lg">
          <ModalHeader toggle={() => { setShowEditModal(false); setEditingDocId(null); }}>Edit Document</ModalHeader>
          <ModalBody>
            <Form>
              <div className={s.formSection}>
                <div className={s.formSectionTitle}>Document Info</div>
                <FormGroup row>
                  <Label sm={3}>Title</Label>
                  <Input sm={9} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Document Type</Label>
                  <Input sm={9} type="select" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}>
                    <option value="policy">Policy</option>
                    <option value="faq">FAQ</option>
                    <option value="sop">SOP</option>
                    <option value="serviceability">Serviceability</option>
                    <option value="pricing_guidance">Pricing Guidance</option>
                  </Input>
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Scope</Label>
                  <Input sm={9} type="select" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                    <option value="global">Global (all customers)</option>
                    <option value="Bangalore">Bangalore</option>
                    <option value="Mangalore">Mangalore</option>
                    <option value="Udupi">Udupi</option>
                    <option value="Hubli">Hubli</option>
                    <option value="Mysore">Mysore</option>
                  </Input>
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Locale</Label>
                  <Input sm={9} type="select" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
                    <option value="en">English</option>
                    <option value="kn">Kannada</option>
                    <option value="hi">Hindi</option>
                    <option value="ta">Tamil</option>
                  </Input>
                </FormGroup>
                <FormGroup row>
                  <Label sm={3}>Version</Label>
                  <Input sm={9} value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
                </FormGroup>
              </div>
              <div className={s.formSection}>
                <div className={s.formSectionTitle}>Content</div>
                <FormGroup>
                  <Input type="textarea" rows={14} className={s.bodyTextarea} value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </FormGroup>
              </div>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => { setShowEditModal(false); setEditingDocId(null); }}>Cancel</Button>
            <Button color="primary" onClick={handleUpdate} disabled={submitting || !form.title.trim() || !form.body.trim()}>
              {submitting ? <Spinner size="sm" /> : 'Update Document'}
            </Button>
          </ModalFooter>
        </Modal>

        {/* View Document Modal */}
        <Modal isOpen={showDetailModal.open} toggle={() => setShowDetailModal({ open: false, doc: null, chunks: [] })} size="lg">
          <ModalHeader toggle={() => setShowDetailModal({ open: false, doc: null, chunks: [] })}>
            {showDetailModal.doc?.title}
          </ModalHeader>
          <ModalBody>
            {showDetailModal.doc && (
              <>
                <div className={s.docDetailHeader}>
                  <span className={`${s.docTypeBadge} ${s[showDetailModal.doc.doc_type] || ''}`}>
                    {(DOC_TYPE_CONFIG[showDetailModal.doc.doc_type] || {}).label || showDetailModal.doc.doc_type}
                  </span>
                  <div className={s.docDetailMeta}>
                    <span className={s.docDetailMetaItem}>Locale: {showDetailModal.doc.locale?.toUpperCase()}</span>
                    <span className={s.docDetailMetaItem}>Version: {showDetailModal.doc.version}</span>
                    <span className={s.docDetailMetaItem}>Updated: {formatDate(showDetailModal.doc.updated_at)}</span>
                  </div>
                </div>
                <h6 className="mb-3">Chunks ({showDetailModal.chunks.length})</h6>
                <div className={s.chunksList}>
                  {showDetailModal.chunks.map((chunk) => (
                    <div key={chunk.id} className={s.chunkCard}>
                      <div className={s.chunkLabel}>Chunk #{chunk.chunk_index}</div>
                      <pre className={s.chunkContent}>{chunk.content}</pre>
                    </div>
                  ))}
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
